"use client";

import useSWRInfinite from "swr/infinite";
import useSWR from "swr";
import {
  Suspense,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Badge,
  Box,
  Button,
  Card,
  Flex,
  Grid,
  Heading,
  IconButton,
  Text,
} from "@radix-ui/themes";
import { observer } from "mobx-react";
import ApiClient from "../../backends/ApiClient";
import { useStores } from "../../stores/useStores";
import FontAwesome from "../../components/utilities/FontAwesome";
import NotificationManager from "../../components/notifications/NotificationManager";
import modalStyles from "../../components/modals/styles/Modal.module.scss";
import { reportError } from "../../utils/errorReporting";
import { useT } from "../../components/utilities/TranslatedText";
import PageIndexSidebar from "./PageIndexSidebar";
import PageIndexList from "./PageIndexList";
import PageIndexBulkBar from "./PageIndexBulkBar";
import styles from "./PageIndexPage.module.scss";

export type PublishedFilterValue = "all" | "published" | "unpublished";

export type PageIndexCategory = {
  id: string;
  category_name: string;
  category_bg_color?: string;
  icon_url?: string;
};

export type PageIndexItem = {
  id: string;
  title: string;
  url: string;
  type: string;
  breadcrumbs: string;
  picture?: string | null;
  categories?: PageIndexCategory[];
  published?: boolean;
  user_id: string;
  parent_id: string;
  path: string;
};

export type PageIndexResponse = {
  results: PageIndexItem[];
  count: number;
  next: string | null;
  previous: string | null;
};

export type AvailableLettersResponse = {
  data: string[];
};

interface PageIndexPageProps {
  initialDataPromise?: Promise<PageIndexResponse>;
  initialLetterPromise?: Promise<AvailableLettersResponse>;
}

const PageIndexPageContent = observer(function PageIndexPageContent({
  initialDataPromise,
  initialLetterPromise,
}: PageIndexPageProps) {
  const t = useT();
  const { organizationStore, userStore, modalStore } = useStores();
  const organizationId = organizationStore.currentOrganization.id;
  const initialData = initialDataPromise ? use(initialDataPromise) : undefined;
  const initialLetter = initialLetterPromise
    ? use(initialLetterPromise)
    : undefined;
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const letterFilterRef = useRef<HTMLElement>(null);

  const [activeLetter, setActiveLetter] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [publishedFilter, setPublishedFilter] =
    useState<PublishedFilterValue>("all");
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [bulkPending, setBulkPending] = useState(false);
  const showPublishedFilter = userStore.editor;
  const showPublishedBadge =
    showPublishedFilter && (publishedFilter !== "all" || selectMode);

  const publishedParam = useMemo(() => {
    if (publishedFilter === "published") return 1;
    if (publishedFilter === "unpublished") return 0;
    return undefined;
  }, [publishedFilter]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Fetch available letters (respects current search filter)
  const { data: lettersData, mutate: mutateLetters } =
    useSWR<AvailableLettersResponse>(
      JSON.stringify({
        key: "page-index-letters",
        organizationId,
        search: debouncedSearch,
        published: publishedParam,
      }),
      async (key: string) => {
        const parsedKey = JSON.parse(key);
        const client = await ApiClient();
        const query: Record<string, unknown> = {};
        if (parsedKey.search) {
          query.search = parsedKey.search;
        }
        if (parsedKey.published !== undefined) {
          query.published = parsedKey.published;
        }
        // the spec doesn't document any query params or a `data` field on the
        // response for this endpoint, but the backend accepts `search` and
        // returns {data: string[]} (SBSB-2912)
        return (await (client.GET(
          "/organizations/{organization_pk}/page-index/available_letters/",
          {
            params: {
              path: { organization_pk: parsedKey.organizationId },
              query: query as never,
            },
          },
        ) as any)) as AvailableLettersResponse;
      },
      {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnMount: !initialLetter,
        fallbackData:
          initialLetter && !debouncedSearch && publishedParam === undefined
            ? initialLetter
            : undefined,
      },
    );

  const availableLetters = useMemo(
    () => lettersData?.data ?? [],
    [lettersData],
  );

  // Clear active letter if it's no longer available (e.g. search narrowed results)
  useEffect(() => {
    if (
      activeLetter &&
      availableLetters.length > 0 &&
      !availableLetters.includes(activeLetter)
    ) {
      setActiveLetter(null);
    }
  }, [activeLetter, availableLetters]);

  const getKey = useCallback(
    (pageIndex: number, previousPageData: PageIndexResponse | null) => {
      if (previousPageData && !previousPageData.next) {
        return null;
      }
      return JSON.stringify({
        key: "page-index",
        organizationId,
        page: pageIndex + 1,
        search: debouncedSearch,
        letter: activeLetter,
        published: publishedParam,
      });
    },
    [organizationId, debouncedSearch, activeLetter, publishedParam],
  );

  const fetcher = useCallback(
    async (key: string): Promise<PageIndexResponse> => {
      const parsedKey = JSON.parse(key);
      const client = await ApiClient();
      const query: Record<string, unknown> = {
        page: parsedKey.page,
      };
      if (parsedKey.search) {
        query.search = parsedKey.search;
      }
      if (parsedKey.letter) {
        query.letter = parsedKey.letter;
      }
      if (parsedKey.published !== undefined) {
        query.published = parsedKey.published;
      }
      return await (client.GET("/organizations/{organization_pk}/page-index/", {
        params: {
          path: { organization_pk: parsedKey.organizationId },
          query: query as never,
        },
      }) as any);
    },
    [],
  );

  // Only use fallback data when no filters are active (initial unfiltered load)
  const hasFilters =
    !!debouncedSearch || !!activeLetter || publishedFilter !== "all";

  const {
    data: pagesData,
    isLoading,
    setSize,
    size,
    isValidating,
    mutate: mutatePages,
  } = useSWRInfinite<PageIndexResponse>(getKey, fetcher, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    revalidateOnMount: !initialData,
    fallbackData: initialData && !hasFilters ? [initialData] : undefined,
  });

  // Flatten all pages into a single array
  const allItems = useMemo(
    () =>
      pagesData?.reduce<PageIndexItem[]>((acc, page) => {
        acc.push(...page.results);
        return acc;
      }, []) ?? [],
    [pagesData],
  );

  // Calculate if there's more to load
  const lastPage = pagesData?.[pagesData.length - 1];
  const hasMore = !!lastPage?.next;
  const isLoadingMore = isValidating && pagesData && pagesData.length < size;

  // IntersectionObserver to trigger loading more when scrolling to bottom
  useEffect(() => {
    const element = loadMoreRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isValidating) {
          setSize(size + 1);
        }
      },
      { threshold: 0.1 },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, isValidating, setSize, size]);

  const handleLetterClick = useCallback((letter: string) => {
    setActiveLetter((prev) => {
      if (letter === "") return null;
      return prev === letter ? null : letter;
    });
  }, []);

  const handleClearAll = useCallback(() => {
    setSearchInput("");
    setActiveLetter(null);
    setPublishedFilter("all");
  }, []);

  const totalCount = pagesData?.[0]?.count ?? 0;
  const resultSummary = `${totalCount} ${t(totalCount === 1 ? "page found" : "pages found")}`;

  const activeFilterCount =
    (publishedFilter !== "all" ? 1 : 0) + (activeLetter ? 1 : 0);

  const filterChips = useMemo(() => {
    const chips: { key: string; label: string; onRemove: () => void }[] = [];
    if (publishedFilter !== "all") {
      chips.push({
        key: "published",
        label:
          publishedFilter === "published" ? t("Published") : t("Unpublished"),
        onRemove: () => setPublishedFilter("all"),
      });
    }
    if (activeLetter) {
      chips.push({
        key: "letter",
        label: `${t("Starts with")} ${activeLetter}`,
        onRemove: () => setActiveLetter(null),
      });
    }
    return chips;
  }, [publishedFilter, activeLetter, t]);

  const selectedItems = useMemo(
    () => allItems.filter((item) => selected[item.id]),
    [allItems, selected],
  );
  const selectedCount = selectedItems.length;
  const selectedEntries = useMemo(
    () => selectedItems.map((item) => ({ id: item.id, title: item.title })),
    [selectedItems],
  );
  const hasSelection = selectedCount > 0;
  const publishedSelectedCount = selectedItems.filter(
    (item) => item.published,
  ).length;
  const isMixedSelection =
    hasSelection &&
    publishedSelectedCount > 0 &&
    publishedSelectedCount < selectedCount;

  const toggleSelected = useCallback((id: string) => {
    setSelected((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  const enterSelectMode = useCallback(() => {
    setSelectMode(true);
  }, []);

  const exitSelectMode = useCallback(() => {
    setSelectMode(false);
    setSelected({});
  }, []);

  const clearSelection = useCallback(() => {
    setSelected({});
  }, []);

  const handleBulkPublish = useCallback(
    async (publish: boolean) => {
      if (!selectedItems.length) return;
      setBulkPending(true);
      try {
        const client = await ApiClient();
        await client.PATCH(
          "/organizations/{organization_pk}/blocks/bulk_partial_update/",
          {
            params: {
              path: {
                organization_pk: organizationStore.currentOrganization.id,
              },
            },
            // the spec types the body as a single Organization, but the backend
            // accepts (and this endpoint requires) an array of partial block updates (SBSB-2912)
            body: selectedItems.map((item) => ({
              id: item.id,
              user_id: item.user_id,
              title: item.title,
              type: item.type,
              parent_id: item.parent_id,
              path: item.path,
              published: publish ? 1 : 0,
            })) as never,
          },
        );
        mutatePages();
        mutateLetters();
      } catch (e) {
        reportError(e, { error_source: "handled" });
        NotificationManager.error(
          `Could not update ${selectedItems.length} page${selectedItems.length === 1 ? "" : "s"}.`,
          "Bulk update failed",
        );
      }
      setBulkPending(false);
    },
    [selectedItems, mutatePages, mutateLetters],
  );

  const requestDelete = useCallback(() => {
    if (!selectedItems.length) return;
    const targetIds = selectedItems.map((item) => item.id);
    const n = targetIds.length;

    const deleteSelectedPages = async () => {
      const results = await Promise.allSettled(
        targetIds.map(async (id) => {
          const client = await ApiClient();
          await client.DELETE("/organizations/{organization_pk}/blocks/{id}/", {
            params: {
              path: { organization_pk: organizationId, id },
            },
          });
          return id;
        }),
      );
      const succeededIds = new Set(
        results
          .filter(
            (result): result is PromiseFulfilledResult<string> =>
              result.status === "fulfilled",
          )
          .map((result) => result.value),
      );
      results.forEach((result) => {
        if (result.status === "rejected") {
          reportError(result.reason, { error_source: "handled" });
        }
      });
      const failedCount = targetIds.length - succeededIds.size;
      if (succeededIds.size) {
        mutatePages();
        mutateLetters();
        setSelected((prev) => {
          const next = { ...prev };
          succeededIds.forEach((id) => delete next[id]);
          return next;
        });
      }
      if (failedCount > 0) {
        NotificationManager.error(
          `Could not delete ${failedCount} page${failedCount === 1 ? "" : "s"}.`,
          "Bulk delete failed",
        );
      }
      modalStore.removeTop();
    };

    modalStore.addModal({
      type: "blockui",
      idleContent: (modalProps) => (
        <div id="sb-fluxRestoreDialog" style={{ cursor: "default" }}>
          <h3 style={{ marginBottom: "1em" }}>
            {`Are you sure you want to delete the following ${selectedItems.length === 1 ? "page" : "pages"}?`}
            <Box pt={"2"}>
              <ul style={{ listStyle: "none" }}>
                {selectedItems.map((item) => (
                  <li key={item.id}>{item.title}</li>
                ))}
              </ul>
            </Box>
          </h3>
          <Flex justify="center" gapX="2">
            <Button variant="outline" onClick={modalStore.removeTop}>
              Cancel
            </Button>
            <Button variant="soft" color="red" onClick={modalProps.onConfirm}>
              Delete {n} page{n === 1 ? "" : "s"}
            </Button>
          </Flex>
        </div>
      ),
      pendingContent: () => (
        <div id="sb-fluxRestoreDialogMessageRestoring">
          <h3>
            <FontAwesome
              name="fa-spinner"
              prefix="fas"
              spin
              style={{ marginRight: "0.5em" }}
            />
            Deleting {n} page{n === 1 ? "" : "s"}...
          </h3>
        </div>
      ),
      failureContent: () => (
        <div id="sb-fluxRestoreDialogMessageRestoreFailed">
          <h3>
            Sorry, but we couldn&apos;t delete{" "}
            {n === 1 ? "that page" : "those pages"}. Please try again later or
            contact an administrator for assistance.
          </h3>
        </div>
      ),
      onConfirm: deleteSelectedPages,
      closeEffect: () => {
        document.body.classList.remove(modalStyles.modalOpen);
      },
    });
  }, [selectedItems, modalStore, mutatePages, mutateLetters]);

  return (
    <Box p={"2"} pt={"4"} className={styles.container}>
      <Grid columns={{ initial: "1fr", sm: "300px 1fr" }} gap="6">
        <PageIndexSidebar
          ref={letterFilterRef}
          letters={availableLetters}
          activeLetter={activeLetter}
          onLetterClick={handleLetterClick}
          disabled={isLoading}
          searchTerm={searchInput}
          onSearchChange={setSearchInput}
          publishedFilter={publishedFilter}
          onPublishedChange={setPublishedFilter}
          showPublishedFilter={showPublishedFilter}
          onClearAll={handleClearAll}
          resultCount={totalCount}
          mobileOpen={mobileFilterOpen}
          onMobileOpenChange={setMobileFilterOpen}
        />

        <Box className={styles.mainContent} minWidth="0">
          <Flex align="center" gap="3" wrap="wrap" mb="3">
            <Box display={{ initial: "inline-block", sm: "none" }}>
              <Button
                variant="surface"
                onClick={() => setMobileFilterOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={mobileFilterOpen}
              >
                <FontAwesome prefix="fas" name="fa-bars" ariaHidden />
                {t("Filters")}
                {activeFilterCount > 0 && (
                  <Badge ml="1" color="blue">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
            </Box>
            <Heading as="h1" size="7" weight="bold">
              {t("Page Directory")}
            </Heading>
            <Box flexGrow="1" />
            {showPublishedFilter && !selectMode && (
              <Button variant="surface" onClick={enterSelectMode}>
                <FontAwesome prefix="fas" name="fa-check" ariaHidden />
                {t("Select multiple pages")}
              </Button>
            )}
          </Flex>

          <Flex wrap="wrap" gap="2" align="center" mb="4">
            <Text size="2" color="gray">
              {resultSummary}
            </Text>
            {filterChips.map((chip) => (
              <Badge
                key={chip.key}
                size="2"
                variant="soft"
                radius="full"
                className={styles.filterChip}
              >
                {chip.label}
                <IconButton
                  size="1"
                  variant="ghost"
                  radius="full"
                  onClick={chip.onRemove}
                  aria-label={`${t("Remove")} ${chip.label} ${t("filter")}`}
                >
                  <FontAwesome prefix="fas" name="fa-xmark" ariaHidden />
                </IconButton>
              </Badge>
            ))}
          </Flex>

          {isLoading && !pagesData ? (
            <Box py="6" style={{ textAlign: "center" }}>
              <Text size="4" color="gray">
                {t("Loading pages...")}
              </Text>
            </Box>
          ) : allItems.length === 0 ? (
            hasFilters ? (
              <Card size="3" mt="4">
                <Flex direction="column" align="center" gap="3" py="6">
                  <Text size="4" color="gray">
                    {t("No pages match these filters.")}
                  </Text>
                  <Button variant="surface" onClick={handleClearAll}>
                    {t("Clear all filters")}
                  </Button>
                </Flex>
              </Card>
            ) : (
              <Box py="6" style={{ textAlign: "center" }}>
                <Text size="4" color="gray">
                  {t("No pages found.")}
                </Text>
              </Box>
            )
          ) : (
            <>
              {showPublishedFilter && selectMode && (
                <Box className={styles.bulkBarSticky}>
                  <PageIndexBulkBar
                    selectedCount={selectedCount}
                    selectedEntries={selectedEntries}
                    hasSelection={hasSelection}
                    bulkPending={bulkPending}
                    isMixedSelection={isMixedSelection}
                    publishedSelectedCount={publishedSelectedCount}
                    onClear={clearSelection}
                    onExit={exitSelectMode}
                    onPublish={() => handleBulkPublish(true)}
                    onUnpublish={() => handleBulkPublish(false)}
                    onDeleteRequest={requestDelete}
                    onRemoveSelected={toggleSelected}
                  />
                </Box>
              )}
              <PageIndexList
                items={allItems}
                showPublishedBadge={showPublishedBadge}
                selectMode={selectMode}
                selected={selected}
                onToggleSelected={toggleSelected}
              />
              <Box ref={loadMoreRef} className={styles.loadMoreTrigger}>
                {isLoadingMore && (
                  <Box py="4" style={{ textAlign: "center" }}>
                    <Text size="3" color="gray">
                      {t("Loading more...")}
                    </Text>
                  </Box>
                )}
              </Box>
            </>
          )}
        </Box>
      </Grid>
    </Box>
  );
});

export default function PageIndexPageClient(props: PageIndexPageProps) {
  const t = useT();
  return (
    <Suspense
      fallback={
        <Box p={"2"} pt={"4"} className={styles.container}>
          <Heading as="h1" size="7" weight="bold" mb="3">
            {t("Page Directory")}
          </Heading>
          <Box py="6" style={{ textAlign: "center" }}>
            <Text size="4" color="gray">
              {t("Loading pages...")}
            </Text>
          </Box>
        </Box>
      }
    >
      <PageIndexPageContent {...props} />
    </Suspense>
  );
}
